import { Pressable, Text, View } from "react-native";

import { PickControl } from "@/components/browse/PickControl";
import { WantMatchBadge } from "@/components/browse/PublicGridCard";
import { CardThumb } from "@/components/inventory/CardThumb";
import { Badge, ConditionChip } from "@/components/ui/Badge";
import type { PublicBrowseItem } from "@/hooks/usePublicBrowse";
import { formatCardIdentity } from "@/lib/catalog/cardIdentity";
import { formatCurrency } from "@/lib/money/money";

/**
 * One browsable item as a list row, the customer-browse counterpart of the inventory's `StockRow`
 * (#341, ADR-0017).
 *
 * It follows `StockRow`'s layout (thumbnail, name with a condition or grade badge, right-aligned
 * price) but is a separate component for the narrower `PublicBrowseItem`. It has no Mkt/Cost/
 * Floor/Ask set, because a customer sees only the tenant's own price (#344), and no gain/loss chip.
 *
 * It draws no rarity dot even though the payload carries rarity (#755). Rarity is the grid's
 * accent, `StockRow` has none either, and the row's one badge slot goes to the condition or grade
 * that says what the customer would buy.
 *
 * No SEALED chip (TRO-1223). It went from the inventory rows because a box already says so in the
 * type it is named for, and it goes from here for the same reason rather than leaving the two
 * lists disagreeing about whether that is worth a chip.
 */
export function PublicListRow({
  item,
  onPress,
  quantity,
  onSetQuantity,
  pickPending = false,
  pickLockedReason = null,
  matchesWant = false,
}: {
  item: PublicBrowseItem;
  onPress?: () => void;
  // How many copies the customer has picked (#646), and the setter for the absolute quantity.
  quantity?: number;
  onSetQuantity?: (next: number) => void;
  pickPending?: boolean;
  /**
   * Why picking is unavailable on the whole storefront, or `null` while it is open (TRO-673).
   *
   * Set once the customer's list is with the Vendor. `PickControl` greys the control and speaks
   * the reason.
   */
  pickLockedReason?: string | null;
  /** Whether this unit meets one of the signed-in person's Wants; the caller decides. */
  matchesWant?: boolean;
}) {
  const gradeLabel = item.grading_company && item.grade
    ? `${item.grading_company} ${item.grade}`
    : "GRADED";

  const conditionWord = item.is_graded ? gradeLabel : item.condition ?? null;
  // Set and collector number (#530) tell two same-name units apart at a glance.
  const identity = formatCardIdentity(item.set_name, item.number, item.variant_label);
  const a11yLabel = [
    item.product_name,
    identity,
    conditionWord,
    item.qty > 1 ? `${item.qty} available` : null,
    item.price != null ? formatCurrency(item.price, item.currency) : "ask vendor for price",
    matchesWant ? "matches your want" : null,
  ].filter(Boolean).join(", ");
  // One right-aligned price at every width, so a phone row stays short and prices are easy to scan.
  const price = (
    <Text
      testID="browse-item-price"
      numberOfLines={1}
      className={item.price != null
        ? "font-num-bold text-base text-primary"
        : "text-xs font-label text-muted-foreground"}
    >
      {item.price != null ? formatCurrency(item.price, item.currency) : "Ask vendor"}
    </Text>
  );

  return (
    // The Card is the row container and the inner Pressable covers only the tap-to-detail area, so
    // the pick control is a sibling rather than a nested Pressable: one tap target and one
    // screen-reader focus per row (#469).
    // A flat row on a hairline, not a Card: a framed card per row cost a phone nearly half its
    // rows, and a list of cards the customer scans by price reads faster as one column.
    <View className="flex-row items-center gap-3 border-b border-border py-2.5">
      <Pressable
        testID="browse-item-row"
        onPress={onPress}
        // "button" only when there is a press to make, but never no role: the composed label is
        // the row's whole announcement, and an element without a role does not reliably carry a
        // name. The read-only public inventory browse (ADR-0056) has no press.
        accessibilityRole={onPress ? "button" : "text"}
        accessibilityLabel={a11yLabel}
        className="min-w-0 flex-1 flex-row items-center gap-3"
      >
        <CardThumb uri={item.image_url} width={44} height={62} label={item.product_name} />
        <View className="min-w-0 flex-1 gap-1">
          <Text
            testID="browse-item-name"
            className="text-[15px] font-title text-foreground"
            numberOfLines={2}
          >
            {item.product_name}
          </Text>
          {
            /* The condition or grade leads the identity line rather than taking a line of its own,
            so a row is as tall as its thumbnail. */
          }
          <View className="flex-row items-center gap-1.5">
            {item.is_graded
              ? <Badge label={gradeLabel} className="bg-accent-soft" textClassName="text-primary" />
              : item.condition
              ? <ConditionChip condition={item.condition} />
              : null}
            {identity
              ? (
                <Text
                  testID="browse-item-identity"
                  className="min-w-0 shrink text-xs font-body text-muted-foreground"
                  numberOfLines={1}
                >
                  {identity}
                </Text>
              )
              : null}
          </View>
          {matchesWant ? <WantMatchBadge /> : null}
          {item.qty > 1
            ? (
              <Text className="text-xs font-label text-muted-foreground">
                {item.qty} available
              </Text>
            )
            : null}
        </View>
        {price}
      </Pressable>
      {onSetQuantity
        ? (
          <PickControl
            productName={item.product_name}
            quantity={quantity ?? 0}
            available={item.qty}
            onChange={onSetQuantity}
            disabled={pickPending}
            lockedReason={pickLockedReason}
          />
        )
        : null}
    </View>
  );
}
